/**
 * Single source of truth for the "Sources officielles et références"
 * section and the HowTo JSON-LD payload of every guide in the
 * Bibliothèque HÉRITA.
 *
 * Each URL is a DEEP link (the exact page on the public source that
 * matches the topic), in line with Google's E-E-A-T recommendations.
 * The descriptions are short, factual and written in French to mirror
 * the tone of the rest of the library.
 */

/* -------- Centralised source presets (re-use across guides) -------- */

const S = {
  // ─── Service-Public.fr — verified pages (2026 migration to .gouv.fr) ───
  SP_DROITS_SUCCESSION: {
    type: 'Service public',
    name: 'Service-Public.fr',
    label: 'Droits de succession — évaluation et calcul',
    description:
      "Outil officiel d'évaluation des droits de succession en fonction de votre lien avec le défunt et de votre situation. Édité par la Direction de l'information légale et administrative.",
    url: 'https://www.service-public.fr/particuliers/vosdroits/F14198',
  },

  // ─── Notaires de France — verified deep pages (2026) ───
  NF_HERITER: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: "Hériter d'une succession",
    description:
      "Guide pédagogique du Conseil supérieur du notariat : ouverture de la succession, acte de notoriété, options successorales et étapes du règlement.",
    url: 'https://www.notaires.fr/fr/heriter-dune-succession',
  },
  NF_REGLEMENT_SUCCESSION: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Déroulement et règlement d\'une succession',
    description:
      'Explication détaillée du déroulement d\'une succession par la profession notariale : actes établis, délais et fiscalité.',
    url: 'https://www.notaires.fr/fr/donation-succession/succession/le-reglement-des-successions',
  },
  NF_DONATION: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Donation : tout ce qu\'il faut savoir',
    description:
      'Présentation complète des donations par les notaires : donation simple, donation-partage, abattements et fiscalité applicable.',
    url: 'https://www.notaires.fr/fr/donation',
  },
  NF_FAIRE_DONATION: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Faire une donation',
    description:
      "Modalités pratiques pour organiser une donation : choix de la forme (don manuel, acte authentique, donation-partage), bénéficiaires et conséquences.",
    url: 'https://www.notaires.fr/fr/faire-une-donation',
  },
  NF_PREPARER_SUCCESSION: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Préparer sa succession',
    description:
      'Conseils officiels du notariat pour organiser la transmission de son patrimoine : testament, donation, assurance-vie, dispositions à prendre.',
    url: 'https://www.notaires.fr/fr/preparer-sa-succession',
  },
  NF_ACHETER: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Acheter un bien immobilier',
    description:
      "Étapes de l'achat immobilier expliquées par les notaires : compromis, conditions suspensives, signature et frais.",
    url: 'https://www.notaires.fr/fr/acheter-un-bien-immobilier',
  },
  NF_VENDRE: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Vendre un bien immobilier',
    description:
      "Guide officiel pour le vendeur : préparation du dossier, diagnostics, signature chez le notaire et fiscalité de la plus-value.",
    url: 'https://www.notaires.fr/fr/vendre-un-bien-immobilier',
  },
  NF_COMPROMIS: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Promesse de vente et compromis de vente',
    description:
      "Différences entre promesse unilatérale et compromis : engagements, délai de rétractation, conditions suspensives.",
    url: 'https://www.notaires.fr/fr/immobilier-fiscalite/achat-et-vente-les-etapes/la-promesse-de-vente-et-le-compromis-de-vente',
  },
  NF_PATRIMOINE_IMMO: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Se constituer un patrimoine immobilier',
    description:
      'Stratégies pour constituer un patrimoine immobilier (achat direct, SCI, démembrement, indivision) et leurs implications successorales.',
    url: 'https://www.notaires.fr/fr/se-constituer-un-patrimoine-immobilier',
  },
  NF_GERER_PROPRIETES: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Gérer ses propriétés immobilières',
    description:
      'Outils notariaux pour gérer ses biens (SCI familiale, indivision conventionnelle, démembrement, baux et locations).',
    url: 'https://www.notaires.fr/fr/gerer-les-proprietes-immobilieres',
  },
  NF_ROLE_NOTAIRE: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Le rôle du notaire',
    description:
      "Missions, devoirs et obligations du notaire en France ; pourquoi le solliciter et ses domaines d'expertise.",
    url: 'https://www.notaires.fr/fr/le-role-du-notaire',
  },
  NF_LEXIQUE_SUCCESSION: {
    type: 'Notariat',
    name: 'Notaires de France',
    label: 'Lexique notarial : la succession',
    description:
      "Définitions officielles du vocabulaire notarial associé à la succession : héritier, ayant droit, dévolution, indivision, soulte…",
    url: 'https://www.notaires.fr/fr/lexique/succession',
  },

  // ─── Légifrance — Code civil & CGI (verified URLs) ───
  // Légifrance bloque les bots ; ces URL fonctionnent en navigateur réel
  // et sont les références canoniques publiées par la République Française.
  LF_ART_730_1_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 730-1 du Code civil',
    description:
      "Disposition de référence : valeur probante de l'acte de notoriété et qualité d'héritier.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006432896',
  },
  LF_ART_1075_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 1075 du Code civil',
    description:
      "Texte fondateur autorisant la donation-partage et précisant ses bénéficiaires possibles.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006436585',
  },
  LF_ART_967_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 967 du Code civil',
    description:
      "Capacité de tester et liberté de disposer de ses biens par testament.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006435760',
  },
  LF_ART_970_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 970 du Code civil',
    description:
      "Conditions de validité du testament olographe : écriture, date et signature de la main du testateur.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006435766',
  },
  LF_ART_815_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 815 du Code civil',
    description:
      "« Nul ne peut être contraint à demeurer dans l'indivision » : le principe fondamental qui ouvre droit au partage.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006432589',
  },
  LF_ART_1583_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 1583 du Code civil',
    description:
      "La vente est parfaite dès accord sur la chose et le prix : disposition fondatrice du droit de la vente.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006441644',
  },
  LF_ART_710_1_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 710-1 du Code civil',
    description:
      "Obligation de forme authentique pour tout acte sujet à publicité foncière (attestation immobilière, vente).",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000023206630',
  },
  LF_ART_1832_CC: {
    type: 'Code civil',
    name: 'Légifrance',
    label: 'Article 1832 du Code civil',
    description:
      "Définition légale de la société : contrat par lequel deux ou plusieurs personnes affectent des biens à une entreprise commune.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006444006',
  },
  LF_ART_800_CGI: {
    type: 'Code général des impôts',
    name: 'Légifrance',
    label: 'Article 800 du CGI',
    description:
      "Obligation déclarative des héritiers et délai légal pour déposer la déclaration de succession.",
    url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000019910812',
  },
};

/* -------- Per-guide registry (verified URLs only) -------- */

export const GUIDE_SEO = {
  '/declaration-succession': {
    sources: [S.SP_DROITS_SUCCESSION, S.NF_HERITER, S.LF_ART_800_CGI],
    howTo: {
      name: "Comment comprendre votre déclaration de succession",
      description:
        "Étapes pour lire et vérifier votre déclaration de succession en s'appuyant sur les sources officielles.",
      totalTime: 'PT8M',
      steps: [
        { name: 'Identifier les héritiers et leur quote-part', text: 'Vérifiez la liste des héritiers et la fraction du patrimoine qui revient à chacun.' },
        { name: 'Lire l\'actif et le passif', text: "Examinez les biens déclarés (actif) puis les dettes déduites (passif) pour reconstituer l'actif net partageable." },
        { name: 'Contrôler les abattements et droits', text: "Vérifiez l'application de votre abattement personnel et le calcul des éventuels droits à payer." },
        { name: 'Repérer les donations antérieures', text: 'Identifiez les donations antérieures rapportables qui peuvent modifier votre quote-part finale.' },
      ],
    },
  },
  '/acte-notoriete': {
    sources: [S.NF_HERITER, S.NF_REGLEMENT_SUCCESSION, S.LF_ART_730_1_CC],
    howTo: {
      name: 'Comment vérifier votre acte de notoriété',
      description:
        "Étapes pour comprendre ce que prouve un acte de notoriété et vérifier son contenu.",
      totalTime: 'PT5M',
      steps: [
        { name: 'Identifier le défunt et la date du décès', text: 'Vérifiez les éléments d\'identité du défunt et la date d\'ouverture de la succession.' },
        { name: 'Vérifier la liste des héritiers', text: 'Confirmez que vous figurez bien dans l\'acte et que les autres héritiers sont correctement identifiés.' },
        { name: 'Lire les options successorales mentionnées', text: 'Repérez les options déjà exercées (acceptation pure et simple, à concurrence de l\'actif net, renonciation).' },
        { name: 'Vérifier les références au testament éventuel', text: 'Si un testament a été déposé au FCDDV, l\'acte le mentionne avec sa nature (olographe, authentique).' },
      ],
    },
  },
  '/donation-partage': {
    sources: [S.NF_DONATION, S.NF_FAIRE_DONATION, S.LF_ART_1075_CC],
    howTo: {
      name: 'Comment lire votre donation-partage',
      description:
        "Étapes pour comprendre un acte de donation-partage et identifier ce que vous recevez.",
      totalTime: 'PT7M',
      steps: [
        { name: 'Identifier le donateur et les attributaires', text: 'Vérifiez l\'identité de la personne qui donne et la liste des bénéficiaires (« attributaires »).' },
        { name: 'Repérer les biens donnés et leur valeur', text: 'Pour chaque bien, notez la valeur retenue dans l\'acte — c\'est elle qui sera figée pour le calcul successoral à venir.' },
        { name: 'Identifier votre lot précis', text: 'Le lot reçu peut être en pleine propriété, en usufruit ou en nue-propriété. Précision essentielle pour vos droits futurs.' },
        { name: 'Vérifier les charges et soultes éventuelles', text: 'Une soulte peut équilibrer les lots ; certaines donations comportent aussi des charges (réversion, droit d\'usage, etc.).' },
      ],
    },
  },
  '/testament': {
    sources: [S.NF_PREPARER_SUCCESSION, S.LF_ART_967_CC, S.LF_ART_970_CC],
    howTo: {
      name: 'Comment vérifier un testament',
      description: "Étapes pour identifier la forme du testament et valider qu'il respecte les conditions légales.",
      totalTime: 'PT6M',
      steps: [
        { name: 'Identifier la forme du testament', text: 'Olographe (manuscrit), authentique (devant notaire) ou mystique (rare). La forme conditionne les exigences de validité.' },
        { name: 'Vérifier les conditions de validité', text: "Pour un testament olographe : écriture, date et signature de la main du testateur (art. 970 du Code civil)." },
        { name: 'Lister les bénéficiaires et legs', text: "Identifiez précisément qui reçoit quoi : legs universel, à titre universel ou particulier." },
        { name: "S'assurer du respect de la réserve héréditaire", text: 'La réserve héréditaire des enfants ne peut pas être atteinte par le testament. Vérifiez que les legs respectent la quotité disponible.' },
      ],
    },
  },
  '/attestation-immobiliere': {
    sources: [S.NF_HERITER, S.NF_REGLEMENT_SUCCESSION, S.LF_ART_710_1_CC],
    howTo: {
      name: "Comment lire une attestation immobilière après décès",
      description: "Étapes pour vérifier le contenu d'une attestation immobilière et vos droits sur le bien transmis.",
      totalTime: 'PT5M',
      steps: [
        { name: 'Identifier le bien et son adresse', text: "Vérifiez la désignation cadastrale et l'adresse du bien transmis." },
        { name: 'Repérer votre quote-part', text: "L'attestation indique la fraction du bien qui vous revient (par exemple 1/2, 1/3, en pleine propriété ou en démembrement)." },
        { name: 'Comprendre le type de propriété', text: "Pleine propriété, usufruit ou nue-propriété : la nature de votre droit détermine ce que vous pouvez faire du bien." },
        { name: 'Vérifier la publicité foncière', text: "L'attestation doit être publiée au Service de la publicité foncière (ex-Conservation des hypothèques) pour être opposable aux tiers." },
      ],
    },
  },
  '/acte-de-vente': {
    sources: [S.NF_ACHETER, S.NF_COMPROMIS, S.LF_ART_1583_CC],
    howTo: {
      name: "Comment vérifier votre acte de vente immobilier",
      description: "Étapes essentielles à contrôler dans un acte de vente avant ou après signature.",
      totalTime: 'PT8M',
      steps: [
        { name: 'Vérifier les parties et le bien', text: 'Identité du vendeur et de l\'acquéreur, désignation cadastrale du bien, surface et description.' },
        { name: 'Contrôler le prix et son paiement', text: 'Prix total, ventilation (immobilier / mobilier), conditions et calendrier de paiement.' },
        { name: 'Identifier les servitudes et droits', text: 'Servitudes de passage, vues, mitoyenneté, droit de préemption éventuel.' },
        { name: 'Lire les annexes obligatoires', text: 'Diagnostics techniques (DPE, plomb, amiante, termites…), titre de propriété et règlement de copropriété si applicable.' },
      ],
    },
  },
  '/sci': {
    sources: [S.NF_PATRIMOINE_IMMO, S.NF_GERER_PROPRIETES, S.LF_ART_1832_CC],
    howTo: {
      name: "Comment comprendre les statuts d'une SCI",
      description: "Étapes pour lire les statuts d'une société civile immobilière et y situer votre rôle.",
      totalTime: 'PT7M',
      steps: [
        { name: 'Identifier les associés et leur apport', text: 'Liste des associés, montant et nature des apports (numéraire, en nature), nombre de parts détenues par chacun.' },
        { name: 'Repérer le gérant et ses pouvoirs', text: 'Identité du gérant, durée du mandat, pouvoirs et limitations statutaires.' },
        { name: 'Comprendre les règles de cession des parts', text: 'Clauses d\'agrément, droit de préemption, conditions de transmission des parts entre associés ou à des tiers.' },
        { name: 'Vérifier le régime fiscal', text: 'IR par défaut (transparence fiscale) ou IS sur option : impact très différent sur la fiscalité de chaque associé.' },
      ],
    },
  },
  '/indivision': {
    sources: [S.NF_HERITER, S.NF_GERER_PROPRIETES, S.LF_ART_815_CC],
    howTo: {
      name: "Comment sortir d'une indivision",
      description: "Étapes pour comprendre vos droits d'indivisaire et engager une sortie d'indivision.",
      totalTime: 'PT7M',
      steps: [
        { name: 'Identifier votre quote-part exacte', text: 'Lisez la fraction du bien indivis qui vous revient (par exemple 1/4) et celle des autres indivisaires.' },
        { name: 'Choisir une option de sortie', text: "Trois voies : vente du bien et partage du prix, rachat par l'un des indivisaires, ou conservation via une convention d'indivision." },
        { name: "Solliciter le partage à l'amiable", text: 'En cas d\'accord entre tous les indivisaires, le partage peut être organisé devant notaire sans procédure judiciaire.' },
        { name: 'Recourir au partage judiciaire si nécessaire', text: "En cas de désaccord persistant, un indivisaire peut saisir le tribunal pour imposer le partage (art. 815 du Code civil)." },
      ],
    },
  },
  '/courrier-notarial': {
    sources: [S.NF_ROLE_NOTAIRE, S.NF_LEXIQUE_SUCCESSION],
    howTo: {
      name: "Comment réagir à un courrier de votre notaire",
      description: "Étapes pour identifier l'objet d'un courrier notarial et y répondre correctement.",
      totalTime: 'PT4M',
      steps: [
        { name: "Identifier l'objet du courrier", text: 'Demande de pièces, convocation, projet d\'acte, demande de paiement de frais, lettre d\'information : chaque type a sa logique propre.' },
        { name: 'Repérer le délai de réponse', text: "Beaucoup de courriers notariaux comportent un délai (réponse, signature, paiement). Le repérer évite des relances et frais supplémentaires." },
        { name: 'Préparer les pièces demandées', text: "État civil, justificatifs de propriété, RIB, copies d'actes antérieurs : rassembler les documents accélère le dossier." },
        { name: 'Demander un rendez-vous si besoin', text: "Un courrier confus ou un acte engageant mérite un rendez-vous d'explication avant toute signature." },
      ],
    },
  },
};

export const guideSeoFor = (path) => GUIDE_SEO[path] || { sources: [], howTo: null };
